import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { showErrorFromMutation, showSuccess } from "@/shared/utils/toast";
import {
  createAITrainerThread,
  deleteAITrainerMemory,
  deleteAITrainerThread,
  fetchAITrainerChat,
  fetchAITrainerMemories,
  fetchAITrainerThreads,
  getChatAnswerAiTrainer,
  pinAITrainerThread,
  renameAITrainerThread,
  updateAITrainerMemory,
} from "../api/aiTrainer";

// get ai trainer chat hook
export const useGetAITrainerChat = ({user_id = "", thread_id = "", prompt = "", display = null, enabled = true, onStreamChunk}) => {
    return useQuery({
      queryKey: ["aiTrainerChat", user_id, thread_id, prompt],
      queryFn: async () => {
        const stream = await getChatAnswerAiTrainer({user_id, thread_id, prompt, display});
        
        if (onStreamChunk && stream) {
          const reader = stream.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
  
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
  
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n");
              
              // Keep the last incomplete line in buffer
              buffer = lines.pop() || "";
  
              // Process complete lines
              for (let line of lines) {
                line = line.trim();
                if (!line) continue;
                if (line.startsWith("data:")) {
                  line = line.replace(/^data:\s*/, "");
                }
                if (line === "[DONE]" || !line) continue;
                try {
                  const chunk = JSON.parse(line);
                  onStreamChunk(chunk);
                } catch (e) {
                  console.warn("Failed to parse chunk:", line);
                }
              }
            }
  
            // Process remaining buffer
            if (buffer.trim()) {
              let line = buffer.trim();
              if (line.startsWith("data:")) {
                line = line.replace(/^data:\s*/, "");
              }
              if (line !== "[DONE]" && line) {
                try {
                  const chunk = JSON.parse(line);
                  onStreamChunk(chunk);
                } catch (e) {
                  console.warn("Failed to parse final chunk:", buffer);
                }
              }
            }
          } finally {
            reader.releaseLock();
          }
        }
  
        return { streamed: true };
      },
      enabled: !!user_id && !!thread_id && !!prompt && enabled,
    });
  }


  // fetch ai trainer chat hook
  export const useFetchAITrainerChatHistory = ({userId = "", threadId = "", enabled = true}) => {
    return useQuery({
      queryKey: ["aiTrainerChatHistory", userId, threadId],
      queryFn: async () => {
        const response = await fetchAITrainerChat({userId, threadId});
        if (response) {
          const messageArray = response
            .split("\n")
            .map((obj) => {
              try {
                return JSON.parse(obj);
              } catch {
                return null;
              }
            })
            .filter((obj) => obj && Object.keys(obj).length > 0);
          return { messages: messageArray };
        }
        return { messages: [] };
      },
      enabled: !!userId && !!threadId && enabled,
      // A thread's history is first fetched the moment the thread is created —
      // before its first message is persisted — so the cache holds an empty
      // list. With the app-wide 5 minute staleTime that empty list was served
      // back on every later visit to the thread, blanking the chat until a
      // reload dropped the cache.
      staleTime: 0,
      refetchOnMount: "always",
    });
  };

export const useAITrainerThreads = ({ userId = "", enabled = true }) =>
  useQuery({
    queryKey: ["aiTrainerThreads", userId],
    queryFn: () => fetchAITrainerThreads({ userId }),
    enabled: !!userId && enabled,
    retry: false,
  });

const useThreadMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["aiTrainerThreads", variables.userId],
      });
    },
    onError: showErrorFromMutation,
  });
};

export const useCreateAITrainerThread = () =>
  useThreadMutation(createAITrainerThread);

export const useRenameAITrainerThread = () =>
  useThreadMutation(renameAITrainerThread);

export const usePinAITrainerThread = () =>
  useThreadMutation(pinAITrainerThread);

export const useDeleteAITrainerThread = () =>
  useThreadMutation(deleteAITrainerThread);

  export const useFetchAITrainerMemories = ({
    userId = "",
    campaignId = "",
    enabled = true,
  }) => {
    return useQuery({
      queryKey: ["aiTrainerMemories", userId, campaignId],
      queryFn: () => fetchAITrainerMemories({userId, campaignId}),
      enabled: !!userId && enabled,
    });
  };

export const useDeleteAITrainerMemory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAITrainerMemory,
    onSuccess: () => {
      showSuccess("Memory deleted");
      queryClient.invalidateQueries({ queryKey: ["aiTrainerMemories"] });
    },
    onError: showErrorFromMutation,
  });
};

export const useUpdateAITrainerMemory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAITrainerMemory,
    onSuccess: () => {
      showSuccess("Memory updated");
      queryClient.invalidateQueries({ queryKey: ["aiTrainerMemories"] });
    },
    onError: showErrorFromMutation,
  });
};
